import {
  BarcodeRecord,
  OrderAction,
  SupplyOrder,
  SupplyOrderItem,
} from "./types";

const statusById: Record<string, string> = {
  "1": "New Order",
  "2": "Viewed",
  "3": "Approved",
  "4": "Barcode Generated",
  "5": "Shipped",
  "6": "Rejected",
};

export const initialSearchQuery = {
  facilityName: "",
  representativeName: "",
  createdBy: "",
  approvedBy: "",
  representativePhone: "",
  shippingAddress: "",
  courierName: "",
  trackingNumber: "",
  status: "",
  isRejected: false,
  rejectedReason: "",
  rejectedDate: null,
  dateofRequest: null,
};

export const sortById = {
  clickedIconData: "Id",
  sortingOrder: "desc",
};

export const queryDisplayTagNames: Record<string, string> = {
  status: "Status",
  facilityName: "Facility Name",
  createdBy: "Requested By",
  representativeName: "Representative Name",
  dateofRequest: "Date of Request",
  approvedBy: "Approved By",
};

export const getApiData = (response: any) => {
  return response?.data?.data ?? response?.data;
};

export const getStatusText = (order?: Partial<SupplyOrder> | null) => {
  if (!order) return "";
  const status = `${order.status ?? ""}`.trim();
  if (statusById[status]) return statusById[status];
  if (order.statusId && statusById[String(order.statusId)]) {
    return statusById[String(order.statusId)];
  }
  return status;
};

export const normalizeStatus = (status?: string | null) => {
  return `${status ?? ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
};

export const getStatusClassName = (order: Partial<SupplyOrder>) => {
  const key = normalizeStatus(getStatusText(order));
  const statusClassMap: Record<string, string> = {
    neworder: "badge-status-new-order",
    needapproval: "badge-status-needs-approval",
    needsapproval: "badge-status-needs-approval",
    viewed: "badge-status-viewed",
    approved: "badge-status-approved",
    barcodegenerated: "badge-status-processing",
    shipped: "badge-status-shipped",
    rejected: "badge-status-rejected",
  };

  return statusClassMap[key] || "badge-status-default";
};

export const isNewOrderStatus = (order: Partial<SupplyOrder>) => {
  return normalizeStatus(getStatusText(order)) === "neworder";
};

export const isViewedStatus = (order: Partial<SupplyOrder>) => {
  return normalizeStatus(getStatusText(order)) === "viewed";
};

export const isApprovedStatus = (order: Partial<SupplyOrder>) => {
  return normalizeStatus(getStatusText(order)) === "approved";
};

export const isBarcodeGeneratedStatus = (order: Partial<SupplyOrder>) => {
  return normalizeStatus(getStatusText(order)) === "barcodegenerated";
};

export const isShippableStatus = (order: Partial<SupplyOrder>) => {
  return isApprovedStatus(order) || isBarcodeGeneratedStatus(order);
};

export const getActionByLabel = (
  actions: OrderAction[],
  labelMatch: string
) => {
  const normalizedMatch = normalizeStatus(labelMatch);
  return actions.find((action) =>
    normalizeStatus(action.label).includes(normalizedMatch)
  );
};

export const getRowAction = (
  order: Partial<SupplyOrder>,
  actions: OrderAction[]
) => {
  if (isViewedStatus(order)) {
    return getActionByLabel(actions, "Approve");
  }

  if (isApprovedStatus(order)) {
    return getActionByLabel(actions, "Generate Barcode");
  }

  return null;
};

export const getBarcodeValue = (record: any): string => {
  if (record === null || record === undefined) return "";
  if (typeof record === "string" || typeof record === "number") {
    return String(record);
  }

  const candidates = [
    record.barcode,
    record.barCode,
    record.barcodeNo,
    record.barCodeNo,
    record.barcodeNumber,
    record.barCodeNumber,
    record.barcodeValue,
    record.value,
    record.label,
    record.specimenId,
    record.accessionNumber,
  ];

  return `${candidates.find(Boolean) ?? ""}`.trim();
};

type BarcodeContext = {
  itemName?: string;
  itemType?: string;
  supplyOrderItemId?: number | string;
  supplyItemId?: number | string | null;
};

const toBarcodeRecord = (
  record: any,
  context: BarcodeContext = {}
): BarcodeRecord => ({
  id: record?.id ?? record?.barcodeId ?? record?.barCodeId,
  value: getBarcodeValue(record),
  itemName:
    record?.itemName ?? record?.supplyItemName ?? context.itemName ?? "",
  itemType: record?.itemType ?? context.itemType ?? "",
  supplyOrderItemId:
    record?.supplyOrderItemId ??
    record?.orderItemId ??
    context.supplyOrderItemId,
  supplyItemId: record?.supplyItemId ?? context.supplyItemId,
  status: record?.status ?? record?.barcodeStatus ?? "",
  generatedDate:
    record?.generatedDate ??
    record?.genratedDate ??
    record?.generatedOn ??
    record?.createdDate ??
    null,
  raw: record,
});

const collectBarcodeCandidates = (
  source: any,
  context: BarcodeContext = {},
  depth = 0,
  allowPrimitive = false
): Array<{ record: any; context: BarcodeContext }> => {
  if (!source || depth > 4) return [];

  if (Array.isArray(source)) {
    return source.flatMap((item) =>
      collectBarcodeCandidates(item, context, depth + 1, allowPrimitive)
    );
  }

  if (typeof source !== "object") {
    return allowPrimitive && getBarcodeValue(source)
      ? [{ record: source, context }]
      : [];
  }

  if (getBarcodeValue(source)) return [{ record: source, context }];

  const nextContext: BarcodeContext = {
    itemName:
      source.itemName ?? source.supplyItemName ?? context.itemName ?? "",
    itemType: source.itemType ?? context.itemType ?? "",
    supplyOrderItemId:
      source.supplyOrderItemId ??
      source.orderItemId ??
      source.id ??
      context.supplyOrderItemId,
    supplyItemId: source.supplyItemId ?? context.supplyItemId,
  };

  return Object.entries(source).flatMap(([key, value]) =>
    collectBarcodeCandidates(
      value,
      nextContext,
      depth + 1,
      ["barcodes", "barCodes", "generatedBarcodes"].includes(key)
    )
  );
};

export const extractBarcodeRecords = (source: any): BarcodeRecord[] => {
  const seen = new Set<string>();
  return collectBarcodeCandidates(source, {}, 0, Array.isArray(source))
    .map(({ record, context }) => toBarcodeRecord(record, context))
    .filter((record) => {
      if (!record.value || seen.has(record.value)) return false;
      seen.add(record.value);
      return true;
    });
};

export const extractBarcodeRecordsFromSupplyItem = (
  item: SupplyOrderItem
): BarcodeRecord[] => {
  const barcodeRows = Array.isArray(item.barcodes)
    ? item.barcodes
    : Array.isArray(item.barCodes)
      ? item.barCodes
      : [];
  const seen = new Set<string>();

  return barcodeRows
    .map((record) =>
      toBarcodeRecord(record, {
        itemName: item.itemName,
        itemType: item.itemType,
        supplyOrderItemId: item.id,
        supplyItemId: item.supplyItemId,
      })
    )
    .filter((record) => {
      if (!record.value || seen.has(record.value)) return false;
      seen.add(record.value);
      return true;
    });
};

export const mergeBarcodeRecords = (
  first: BarcodeRecord[],
  second: BarcodeRecord[]
) => {
  const seen = new Set<string>();
  return [...first, ...second].filter((record) => {
    if (!record.value || seen.has(record.value)) return false;
    seen.add(record.value);
    return true;
  });
};

export const formatForInputDate = (inputDate: any) => {
  if (!inputDate) return "";

  if (typeof inputDate === "string") {
    return inputDate.slice(0, 10);
  }

  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-CA");
};

export const toNumberOrNull = (value: any) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
